import React, { useEffect, useRef, useState} from 'react';
import 'react-phone-number-input/style.css';

import Appearance from 'styles/Appearance.js';
import PhoneInput from 'react-phone-number-input';
import ProgressBar from 'views/ProgressBar.js';

const TextField = React.forwardRef(({
    append, appendContent, autoCapitalize, autoComplete, autoCorrect, autoHighlight = false, canShowSensitiveContent = true, children, className, containerStyle, custom = false, defaultValue, disabled, expandWithText, fieldStyle, format, focused = false, icon, iconStyle, insetLabel, insetLabelStyle, insetContainerStyle, isSecure = false, loading, onChange, onBlur, onKeyDown, onClick, onFocus, placeholder, prepend, prependContent, secureVisibilityState = false, spellCheck, useDelay, value
}, outerRef) => {

    const containerRef = useRef(null);
    const innerRef = useRef(null);
    const ref = outerRef || innerRef;

    const [active, setActive] = useState(focused);
    const [countryCode, setCountryCode] = useState(null);
    const [textTimeout, setTextTimeout] = useState(false);
    const [text, setText] = useState(value !== null && value !== undefined ? value : '');
    const [visible, setVisible] = useState(secureVisibilityState);

    const onBodyClick = evt => {
        if(containerRef.current?.contains(evt.target) === false) {
            setActive(false);
        }
    }

    const onFieldClick = evt => {
        setActive(true);
        if(typeof(onClick) === 'function') {
            onClick(evt);
        }
    }

    const onFieldBlur = () => {
        setActive(false);
        if(typeof(onBlur) === 'function') {
            onBlur();
        }
    }

    const onFocusChange = () => {
        setActive(true);
        if(typeof(onFocus) === 'function') {
            onFocus();
        }
    }

    const onPhoneInputTextChange = text => {

        // clear previous timeout if present
        if(textTimeout) {
            clearTimeout(textTimeout);
        }

        // set text with timeout if applicable
        setText(text);
        setTextTimeout(setTimeout(() => {
            if(typeof(onChange) === 'function') {
                onChange(text);
            }
        }, useDelay === true ? 250 : 0));
    }

    const onStaticFieldClick = evt => {
        onFieldClick(evt);
        if(typeof(onFocus) === 'function') {
            onFocus(evt);
        }
    }

    const onTextChange = evt => {

        // clear previous timeout if present
        if(textTimeout) {
            clearTimeout(textTimeout);
        }

        let newText = evt.target.value.replace(prepend || '', '').replace(append || '', '');
        switch(format) {
            case 'double':
            case 'number':
            newText = parseFloat(newText.replace(/[^\d.-]/g,''));
            break;

            case 'integer':
            newText = parseInt(newText.replace(/[^\d.-]/g,''));
            break;

            default:
            if(typeof(format) === 'function') {
                newText = format(newText);
            }
        }

        // set text with timeout if applicable
        setText(newText);
        setTextTimeout(setTimeout(() => {
            if(typeof(onChange) === 'function') {
                onChange(newText);
            }
        }, useDelay === true ? 250 : 0));
    }

    const onKeyDownPress = evt => {
        let keyCode = evt.keyCode;
        setTimeout(() => {
            if(typeof(onKeyDown) === 'function') {
                if(keyCode === 8) {
                    onKeyDown('backspace');
                    return;
                }
                onKeyDown(keyCode);
            }
        }, useDelay === false ? 0 : 250);
    }

    const getClickableFieldValue = () => {
        if(isSecure === false || visible === true) {
            return value || placeholder;
        }
        return value ? value.split('').map(() => '•').join('') : placeholder;
    }

    const getContent = () => {
        if(format !== 'phone_number') {
            return getField();
        }
        return (
            <div
            className={`dummy-field ${window.theme} ${active ? 'active' : ''} ${className || ''}`}
            ref={containerRef}
            style={{
                alignItems: 'center',
                backgroundColor: disabled ? Appearance.colors.divider() : Appearance.colors.textField(),
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
                height: expandWithText ? 'auto' : 35,
                minHeight: 35,
                position: 'relative',
                ...containerStyle
            }}>
                <PhoneInput
                value={text}
                defaultCountry={countryCode}
                placeholder={placeholder}
                international={true}
                countryCallingCodeEditable={false}
                onChange={onPhoneInputTextChange}
                style={{
                    width: '100%'
                }}/>
                {appendContent}
            </div>
        )
    }

    const getField = () => {

        let isClickableField = typeof(onClick) === 'function';
        if(custom === true) {
            return (
                <div
                className={`dummy-field ${window.theme} ${active ? 'active':''} ${className || ''} ${isClickableField ? 'cursor-pointer' : ''}`}
                onClick={isClickableField ? onStaticFieldClick : null}
                ref={containerRef}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    flexGrow: 1,
                    height: 35,
                    minHeight: 35,
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%',
                    ...containerStyle
                }}>
                   {children} 
                </div>
            )
        }
        return (
            <div
            className={`dummy-field ${window.theme} ${active ? 'active':''} ${className || ''} ${isClickableField ? 'cursor-pointer' : ''}`}
            onClick={isClickableField ? onStaticFieldClick : null}
            ref={containerRef}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
                height: expandWithText ? 'auto' : 35,
                minHeight: 35,
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
                ...disabled && {
                    backgroundColor: Appearance.colors.divider()
                },
                ...containerStyle
            }}>
                {getIcon(icon) && (
                    <div style={{
                        paddingRight: 8,
                        marginLeft: 4
                    }}>
                        <i
                        className={getIcon(icon)}
                        style={{
                            color: Appearance.colors.icon(),
                            fontSize: 13,
                            ...iconStyle
                        }}/>
                    </div>
                )}
                {insetLabel && (
                    <div style={{
                        marginRight: 8,
                        paddingRight: 8,
                        borderRight: `1px solid ${Appearance.colors.softBorder()}`,
                        ...insetContainerStyle
                    }}>
                        <span style={{
                            fontSize: 10,
                            fontWeight: '600',
                            color: Appearance.colors.subText(),
                            ...insetLabelStyle
                        }}>{insetLabel}</span>
                    </div>
                )}
                {prependContent}
                {getFieldComponent()}
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2
                }}>
                    <div style={{
                        display: loading ? 'block' : 'none',
                        overflow: 'hidden',
                        borderRadius: 2,
                        height: 2
                    }}>
                        <ProgressBar />
                    </div>
                </div>
                {appendContent}
                {isSecure === true && canShowSensitiveContent === true && (
                    <img
                    className={'cursor-pointer'}
                    onClick={setVisible.bind(this, !visible)}
                    src={visible ? `images/visible-eye-${window.theme === 'dark' ? 'white' : 'grey'}.png` : `images/non-visible-eye-${window.theme === 'dark' ? 'white' : 'grey'}.png`}
                    style={{
                        height: 25,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 25
                    }} />
                )}
            </div>
        )
    }

    const getFieldComponent = () => {
        if(typeof(onClick) === 'function') {
            return (
                <div style={{
                    flexGrow: 1,
                    position: 'relative'
                }}>
                    <input 
                    onBlur={onFieldBlur}
                    onFocus={onStaticFieldClick}
                    style={{ 
                        position: 'absolute',
                        left: '-9999px',
                        width: 1,
                        height: 1,
                        opacity: 0,
                        overflow: 'hidden'
                    }} />
                    <span style={{
                        backgroundColor: Appearance.colors.transparent,
                        borderWidth: 0,
                        color: value ? Appearance.colors.text() : Appearance.colors.subText(),
                        fontSize: 12,
                        fontWeight: 500,
                        zIndex: 1,
                        ...fieldStyle
                    }}>{getClickableFieldValue()}</span>
                </div>
            )
        }
        if(isSecure || expandWithText !== true) {
            return (
                <input
                autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
                autoComplete={autoComplete || 'off'}
                autoCorrect={autoCorrect === false ? 'off':'on'}
                className={`dummy-field-input ${window.theme} ${disabled ? 'disabled' : ''}`}
                disabled={disabled}
                inputMode={getInputMode()}
                onBlur={onFieldBlur}
                onClick={onFieldClick}
                onChange={onTextChange}
                onFocus={onFocusChange}
                onKeyDown={onKeyDownPress}
                ref={ref}
                value={(prepend || '') + (text || '') + (append || '')}
                placeholder={placeholder || ''}
                spellCheck={spellCheck === false ? 'false' : 'true'}
                type={isSecure === true && visible === false ? 'password' : 'text'}
                style={{
                    border: 'none',
                    width: '100%',
                    height: 'auto',
                    fontSize: 12,
                    fontWeight: 500,
                    color: Appearance.colors.text(),
                    backgroundColor: Appearance.colors.transparent,
                    ...fieldStyle
                }} />
            )
        }

        return (
            <textarea
            ref={ref}
            className={`dummy-field-input ${window.theme}`}
            onBlur={onFieldBlur}
            onClick={onFieldClick}
            onChange={onTextChange}
            onFocus={onFocusChange}
            onKeyDown={onKeyDownPress}
            value={(prepend || '') + (text || '') + (append || '')}
            placeholder={placeholder || ''}
            type={isSecure === true ? 'password':'text'}
            autoComplete={autoComplete || 'off'}
            autoCorrect={autoCorrect === false ? 'off':'on'}
            autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
            spellCheck={spellCheck === false ? 'false':'true'}
            style={{
                border: 'none',
                width: '100%',
                height: 'auto',
                minHeight: 125,
                fontSize: 12,
                fontWeight: 500,
                resize: 'none',
                verticalAlign: 'middle',
                color: Appearance.colors.text(),
                paddingTop: expandWithText ? 0 : 18.5,
                backgroundColor: Appearance.colors.transparent,
                ...fieldStyle
            }} />
        )
    }

    const getInputMode = () => {
        if([ 'integer' ].includes(format)) {
            return 'numeric';
        }
        if([ 'double', 'integer', 'number', 'phone_number' ].includes(format)) {
            return 'decimal';
        }
        return null;
    }

    const setupFormat = () => {
        if(format === 'phone_number') {
            try {
                let locale = new Intl.Locale(navigator.language);
                setCountryCode(locale.region);
            } catch(e) {
                console.error(e.message);
                setCountryCode('US');
            }
        }
    }

    useEffect(() => {
        setupFormat();
    }, [format]);

    useEffect(() => {
        setActive(focused);
    }, [focused])

    useEffect(() => {
        setText(value || '');
    }, [value]);

    useEffect(() => {
        setText(value || defaultValue || '');
    }, [defaultValue]);

    useEffect(() => {
        autoHighlight && ref.current?.select();
    }, [ref.current]);

    useEffect(() => {
        const inputElement = ref.current;
        
        // Handle input event for autofill and manual input
        const handleInput = (evt) => {
            const newValue = evt.target.value.replace(prepend || '', '').replace(append || '', '');
            if (newValue !== text) {
                setText(newValue);
                if (typeof(onChange) === 'function') {
                    onChange(newValue);
                }
            }
        };

        if (inputElement) {
            inputElement.addEventListener('input', handleInput);
        }

        return () => {
            if (inputElement) {
                inputElement.removeEventListener('input', handleInput);
            }
        };
    }, [ref.current, text, onChange, prepend, append]);

    useEffect(() => {
        if(typeof(onClick) === 'function') {
            window.document.addEventListener('click', onBodyClick);
            return () => {
                window.document.removeEventListener('click', onBodyClick);
            }
        }
    }, []);

    return getContent()
})

export default TextField;

export const getIcon = (icon) => {
    switch(icon) {

        case 'user':
        return 'fas fa-user';

        case 'spouse':
        return 'fas fa-house-user';

        case 'user-secret':
        return 'fas fa-user-secret';

        case 'email':
        return 'fas fa-envelope';

        case 'phone':
        return 'fas fa-phone';

        case 'calendar':
        return 'fas fa-calendar-alt';

        case 'house':
        return 'fas fa-home';

        case 'search':
        return 'fas fa-search';

        case 'lock':
        return 'fas fa-lock';

        case 'location':
        return 'fas fa-location-arrow';

        case 'payment':
        return 'fas fa-money-check-alt';

        case 'asterick':
        return 'fas fa-asterisk';

        case 'route':
        return 'fas fa-route';

        case 'guardian':
        return 'fas fa-user-friends';

        case 'title':
        return 'fas fa-signature';

        case 'description':
        return 'fas fa-stream';

        case 'featured':
        return 'fas fa-hand-holding-heart';

        case 'wifi':
        return 'fas fa-wifi';

        case 'pattern':
        return 'fas fa-th'

        case 'office':
        return 'fas fa-briefcase'

        default:
        return;
    }
}
